/**
 * Centralized query key factory for TanStack Query.
 *
 * Usage:
 *   queryKey: queryKeys.members.list()
 *   queryKey: queryKeys.members.detail(memberId)
 *   invalidateQueries({ queryKey: queryKeys.members.all })
 *
 * Naming convention: queryKeys.<domain>.<scope>(<params>)
 * - .all — broadest invalidation scope (invalidates list + all details)
 * - .list() — list queries (optionally with filters)
 * - .detail(id) — single entity queries
 */
export const queryKeys = {
  // ─── Admin Membership ──────────────────────────────────────────────────────
  members: {
    all: ["members"] as const,
    list: () => ["members", "list"] as const,
    detail: (id: string) => ["members", "detail", id] as const,
    applications: (id: string) => ["members", "applications", id] as const,
    pendingApplications: () => ["members", "pendingApplications"] as const,
    noProfile: () => ["members", "noProfile"] as const,
    rejectReady: () => ["members", "rejectReady"] as const,
    audits: () => ["members", "audits"] as const,
    latestApplication: () => ["members", "latestApplication"] as const,
    applicationDetail: (id: string) => ["members", "applicationDetail", id] as const,
  },

  // ─── Applications ──────────────────────────────────────────────────────────
  applications: {
    all: ["applications"] as const,
    detail: (id: string) => ["applications", "detail", id] as const,
  },

  // ─── SoW ───────────────────────────────────────────────────────────────────
  sows: {
    all: ["sows"] as const,
    list: () => ["sows", "list"] as const,
    detail: (id: string) => ["sows", "detail", id] as const,
    requests: () => ["sows", "requests"] as const,
    signingStatus: (sowId: string) => ["sows", "signing-status", sowId] as const,
  },

  // ─── Invoices ──────────────────────────────────────────────────────────────
  invoices: {
    all: ["invoices"] as const,
    list: () => ["invoices", "list"] as const,
    detail: (id: string) => ["invoices", "detail", id] as const,
    spList: () => ["invoices", "spList"] as const,
    spDetail: (id: string) => ["invoices", "spDetail", id] as const,
  },

  // ─── Growth Coach ──────────────────────────────────────────────────────────
  growthCoach: {
    all: ["growthCoach"] as const,
    detail: (id: string) => ["growthCoach", "detail", id] as const,
    preMeeting: () => ["growthCoach", "preMeeting"] as const,
    inProgress: () => ["growthCoach", "inProgress"] as const,
    recommendations: () => ["growthCoach", "recommendations"] as const,
    timeReport: () => ["growthCoach", "timeReport"] as const,
    allAssessments: () => ["growthCoach", "allAssessments"] as const,
  },

  // ─── IP Expert ─────────────────────────────────────────────────────────────
  ipExpert: {
    all: ["ipExpert"] as const,
    assessments: () => ["ipExpert", "assessments"] as const,
    assessmentDetail: (id: string) => ["ipExpert", "assessmentDetail", id] as const,
    inProgress: () => ["ipExpert", "inProgress"] as const,
    submitted: () => ["ipExpert", "submitted"] as const,
    submittedDetail: (id: string) => ["ipExpert", "submittedDetail", id] as const,
    dueDiligence: () => ["ipExpert", "dueDiligence"] as const,
    dueDiligenceDetail: (id: string) => ["ipExpert", "dueDiligenceDetail", id] as const,
    recommendations: () => ["ipExpert", "recommendations"] as const,
    sowKickoff: () => ["ipExpert", "sowKickoff"] as const,
  },

  // ─── Service Provider ──────────────────────────────────────────────────────
  providers: {
    all: ["providers"] as const,
    firms: () => ["providers", "firms"] as const,
    firmDetail: (id: string) => ["providers", "firmDetail", id] as const,
    firmProfile: () => ["providers", "firmProfile"] as const,
    practitioners: () => ["providers", "practitioners"] as const,
  },

  // ─── Programs ──────────────────────────────────────────────────────────────
  programs: {
    all: ["programs"] as const,
    applications: (id: string) => ["programs", "applications", id] as const,
  },

  // ─── Dashboards ────────────────────────────────────────────────────────────
  // `all` is the broad-sweep cascade key — invalidating it hits every role
  // dashboard via TanStack Query's prefix matching. Cross-role mutations
  // (anything that mutates data viewable from another role) MUST include
  // queryKeys.dashboards.all in their invalidateKeys. See CLAUDE.md → State
  // Sync Discipline for the rationale.
  dashboards: {
    all: ["dashboards"] as const,
    admin: () => ["dashboards", "admin"] as const,
    // Region stat cards (getRegionStatsAsync). Extends the admin() prefix —
    // NOT the same exact key, which is bound to a different queryFn
    // (getMemberOwnerHeaders) — so every dashboards.admin() / dashboards.all
    // invalidation cascades here via prefix matching.
    adminStats: () => ["dashboards", "admin", "stats"] as const,
    founder: () => ["dashboards", "founder"] as const,
    expert: () => ["dashboards", "expert"] as const,
    provider: () => ["dashboards", "provider"] as const,
    growthCoach: () => ["dashboards", "growthCoach"] as const,
  },

  // ─── Email ─────────────────────────────────────────────────────────────────
  email: {
    all: ["email"] as const,
    templates: () => ["email", "templates"] as const,
    templateDetail: (id: string) => ["email", "templateDetail", id] as const,
    logs: () => ["email", "logs"] as const,
  },

  // ─── Region (org) Settings ─────────────────────────────────────────────────
  regionSettings: {
    all: ["regionSettings"] as const,
    orgEmailSettings: () => ["regionSettings", "orgEmailSettings"] as const,
  },

  // ─── Admin Profiles ────────────────────────────────────────────────────────
  adminProfiles: {
    all: ["adminProfiles"] as const,
    list: () => ["adminProfiles", "list"] as const,
    detail: () => ["adminProfiles", "detail"] as const,
    emailPreferences: () => ["adminProfiles", "emailPreferences"] as const,
  },

  // ─── Stats ─────────────────────────────────────────────────────────────────
  stats: {
    admin: () => ["stats", "admin"] as const,
    founder: () => ["stats", "founder"] as const,
    expert: () => ["stats", "expert"] as const,
  },

  // ─── Notes ─────────────────────────────────────────────────────────────────
  notes: {
    all: ["notes"] as const,
    admin: (id: string) => ["notes", "admin", id] as const,
    expert: (id: string) => ["notes", "expert", id] as const,
  },

  // ─── Startups ──────────────────────────────────────────────────────────────
  startups: {
    all: ["startups"] as const,
    list: () => ["startups", "list"] as const,
    detail: (id: string) => ["startups", "detail", id] as const,
  },

  // ─── Due Diligence (automated screening) ───────────────────────────────────
  dd: {
    all: ["dd"] as const,
    byApplication: (applicationId: string) => ["dd", "application", applicationId] as const,
    run: (runId: string) => ["dd", "run", runId] as const,
    preflight: (applicationId: string) => ["dd", "preflight", applicationId] as const,
    // IP-expert read-only latest run. Extends the byApplication prefix so an admin
    // re-run/re-evaluate (which invalidates dd.byApplication) cascades here too,
    // without colliding with the admin's own byApplication query (different shape).
    expertLatest: (applicationId: string) => ["dd", "application", applicationId, "expertLatest"] as const,
  },

  // ─── COI list (Audits → COI Management) ────────────────────────────────────
  coi: {
    all: ["coi"] as const,
    list: () => ["coi", "list"] as const,
    importEvents: () => ["coi", "importEvents"] as const,
  },

  // ─── Misc ──────────────────────────────────────────────────────────────────
  invitations: () => ["invitations"] as const,
  intakeStatus: () => ["intakeStatus"] as const,
} as const;
